import { useDeleteAdminUserMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { HGAdminUser } from "@/common/types";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";

export default function AdminUserDeleteDialog({
  open,
  onOpenChange,
  user,
  onError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: HGAdminUser;
  onError: (message: string | null) => void;
}) {
  const [deleteUser, { isLoading }] = useDeleteAdminUserMutation();

  const handleDelete = async () => {
    onError(null);
    try {
      await deleteUser(user.id).unwrap();
      onOpenChange(false);
    } catch (e) {
      // A 409 with counts ("owns 3 collections and 42 records") comes back
      // here and suggests disabling instead -- surfaced as page-level status
      // since this dialog is about to close either way.
      onError(
        getApiErrorDetail(
          e,
          "Could not delete that account. Please try again.",
        ),
      );
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete account</DialogTitle>
        </DialogHeader>
        <div>
          Are you sure you want to delete
          <span className="font-semibold">
            {" " + (user.nickname || user.username || user.id)}
          </span>
          ? Accounts that own collections or records cannot be deleted --
          disable them instead.
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={isLoading}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
